import {
    AfterViewInit,
    Directive,
    ElementRef,
    EventEmitter,
    forwardRef,
    Inject,
    Injectable,
    Injector,
    Input,
    NgZone,
    OnInit,
    Output,
    PLATFORM_ID,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { AbstractControl, ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR, NgControl, Validators } from '@angular/forms';

import { GrecaptchaService } from 'lib/services/google/grecaptcha.service';

declare const grecaptcha: any;

declare global {
    interface Window {
        grecaptcha: any;
        reCaptchaLoad: () => void;
    }
}

// https://netbasal.com/how-to-integrate-recaptcha-in-your-angular-forms-400c43344d5c <- for larger picture

@Injectable()
export class ReCaptchaAsyncValidator {
    constructor(private grecaptchaService: GrecaptchaService) {}

    validateToken(token: string) {
        return (_: AbstractControl) => {
            return this.grecaptchaService.isValid(token);
        };
    }
}

export interface ReCaptchaConfig {
    theme?: 'dark' | 'light';
    type?: 'audio' | 'image';
    size?: 'compact' | 'normal';
    tabindex?: number;
}

@Directive({
    selector: '[nbRecaptcha]',
    exportAs: 'nbRecaptcha',
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => ReCaptchaDirective),
            multi: true,
        },
        ReCaptchaAsyncValidator,
    ],
    standalone: false,
})
export class ReCaptchaDirective implements OnInit, AfterViewInit, ControlValueAccessor {
    @Input() key: string;
    @Input() config: ReCaptchaConfig = {};
    @Input() lang: string;

    @Output() captchaResponse = new EventEmitter<string>();
    @Output() captchaExpired = new EventEmitter();

    private control: FormControl;
    private widgetId: number;

    private onChange: (value: string) => void;
    private onTouched: (value: string) => void;

    isBrowser: boolean;

    constructor(
        @Inject(PLATFORM_ID) private platformId: Object,
        private grecaptchaService: GrecaptchaService,
        private element: ElementRef,
        private ngZone: NgZone,
        private injector: Injector,
        private reCaptchaAsyncValidator: ReCaptchaAsyncValidator
    ) {
        this.isBrowser = isPlatformBrowser(this.platformId);
    }

    ngOnInit() {
        this.registerReCaptchaCallback();
    }

    registerReCaptchaCallback() {
        const register = () => {
            const config = {
                sitekey: this.key,
                callback: this.onSuccess.bind(this),
                'expired-callback': this.onExpired.bind(this),
            };
            this.widgetId = this.render(this.element.nativeElement, config);
        };
        if (!this.isBrowser) {
            return;
        }
        window.reCaptchaLoad = () => {
            return register();
        };
        register();
    }

    ngAfterViewInit() {
        this.control = this.injector.get(NgControl).control as FormControl;
        this.setValidators();
    }

    /**
     * Useful for multiple captcha
     * @returns {number}
     */
    getId() {
        return this.widgetId;
    }

    /**
     * Calling the setValidators doesn't trigger any update or value change event.
     * Therefore, we need to call updateValueAndValidity to trigger the update
     */
    private setValidators() {
        this.control.setValidators(Validators.required);
        this.control.updateValueAndValidity();
    }

    writeValue(obj: any): void {}

    registerOnChange(fn: any): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }

    /**
     * onExpired
     */
    onExpired() {
        this.ngZone.run(() => {
            this.captchaExpired.emit();
            this.onChange(null);
            this.onTouched(null);
        });
    }

    /**
     *
     * @param response
     */
    onSuccess(token: string) {
        this.ngZone.run(() => {
            this.verifyToken(token);
            this.captchaResponse.next(token);
            this.onChange(token);
            this.onTouched(token);
        });
    }

    /**
     *
     * @param token
     */
    verifyToken(token: string) {
        this.control.setAsyncValidators(this.reCaptchaAsyncValidator.validateToken(token));
        this.control.updateValueAndValidity();
    }

    /**
     * Renders the container as a reCAPTCHA widget and returns the ID of the newly created widget.
     * @param element
     * @param config
     * @returns {number}
     */
    private render(element: HTMLElement, config): number {
        return grecaptcha.render(element, config);
    }

    /**
     * Resets the reCAPTCHA widget.
     */
    reset(): void {
        if (!this.widgetId) return;
        grecaptcha.reset(this.widgetId);
        this.onChange(null);
    }

    /**
     * Gets the response for the reCAPTCHA widget.
     * @returns {string}
     */
    getResponse(): string {
        if (!this.widgetId) {
            return grecaptcha.getResponse(this.widgetId);
        }
    }
}
